import React, { useMemo, useState } from "react";

import {
	Badge,
	Button,
	InfoMessage,
	Spinner,
	Table,
	TTableColumn,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import AdminService from "@/services/AdminService";

import { formatPrice } from "@/utils/USubscription";

import { adminKeys, subscriptionKeys } from "@/constants/CQueryKeys";

import {
	TAdminPlan,
	TAdminPlanPrice,
	TAdminPlanPriceStatus,
} from "@/types/TAdmin";

import { promiseToast } from "@/modules/index";

import { AdminPlanEditModal } from "./components/AdminPlanEditModal";

const STATUS_COLOR: Record<
	TAdminPlanPriceStatus,
	"success" | "warning" | "danger" | "neutral"
> = {
	synced: "success",
	diverged: "warning",
	missing: "danger",
	unknown: "neutral",
};

const limit = (value: number | null) => (value === null ? "∞" : String(value));

const PriceCell: React.FC<{
	price: TAdminPlanPrice | null;
	currency: string;
}> = ({ price, currency }) => {
	if (!price) return <span className="text-neutral-400">—</span>;
	return (
		<span
			className="flex flex-col gap-0.5"
			data-testid={`price-${price.lookupKey}`}
		>
			<span className="flex items-center gap-2">
				<span>
					{price.amount === null
						? "—"
						: formatPrice(price.amount, currency)}
				</span>
				<Badge
					color={STATUS_COLOR[price.status]}
					variant="tint"
					size="sm"
					title={price.lookupKey}
				>
					{t(`admin.plans.status.${price.status}`)}
				</Badge>
			</span>
			{price.status === "diverged" && (
				<span className="text-xs text-neutral-500">
					{t("admin.plans.stripeHas", {
						amount:
							price.stripeAmount === null
								? "—"
								: formatPrice(price.stripeAmount, currency),
						id: price.stripePriceId,
					})}
				</span>
			)}
			<span className="font-mono text-xs text-neutral-400">
				{price.priceId ?? "—"}
			</span>
		</span>
	);
};

/**
 * The plans tab: limits and flags (edited in a modal, after a dry run
 * that says what the change reaches), and per sold price whether Stripe
 * still agrees with what we hold. Nothing here sets an amount — a price
 * changes in the Stripe dashboard, then "sync" pulls it over.
 */
export const AdminPlans: React.FC = () => {
	const queryClient = useQueryClient();
	const [editing, setEditing] = useState<TAdminPlan | null>(null);
	const { data, isLoading, error } = useQuery({
		queryKey: adminKeys.plans,
		queryFn: AdminService.plans,
	});

	const sync = useMutation({
		mutationFn: AdminService.syncPlans,
		onSuccess: (result) => {
			queryClient.setQueryData(adminKeys.plans, result);
			// The pricing grid everyone sees reads the same rows.
			void queryClient.invalidateQueries({
				queryKey: subscriptionKeys.plans,
			});
		},
	});

	// The 409 for an incomplete catalogue names the missing lookup keys.
	const runSync = () => {
		promiseToast(sync.mutateAsync(), {
			success: t("admin.plans.synced"),
		});
	};

	const columns: TTableColumn<TAdminPlan>[] = useMemo(
		() => [
			{
				key: "name",
				label: t("admin.plans.columns.plan"),
				render: (plan) => (
					<span className="flex flex-col">
						<span className="font-medium">{plan.name}</span>
						<span className="font-mono text-xs text-neutral-400">
							{plan.code}
							{!plan.isPublic && ` · ${t("admin.plans.private")}`}
						</span>
					</span>
				),
			},
			{
				key: "maxProjects",
				label: t("admin.plans.columns.limits"),
				truncate: false,
				render: (plan) => (
					<span className="text-sm">
						{t("admin.plans.limits", {
							projects: limit(plan.maxProjects),
							companies: limit(plan.maxCompanies),
							clients: limit(plan.maxClients),
							members: limit(plan.maxMembers),
						})}
					</span>
				),
			},
			{
				key: "canExportPdf",
				label: t("admin.plans.columns.features"),
				truncate: false,
				render: (plan) => (
					<span className="flex flex-wrap gap-1">
						{plan.canExportPdf && (
							<Badge size="sm" variant="outline" color="neutral">
								PDF
							</Badge>
						)}
						{plan.canInvoice && (
							<Badge size="sm" variant="outline" color="neutral">
								{t("admin.plans.features.invoices")}
							</Badge>
						)}
						{plan.canShare && (
							<Badge size="sm" variant="outline" color="neutral">
								{t("admin.plans.features.sharing")}
							</Badge>
						)}
					</span>
				),
			},
			{
				key: "month",
				label: t("admin.plans.columns.month"),
				truncate: false,
				render: (plan) => (
					<PriceCell price={plan.month} currency={plan.currency} />
				),
			},
			{
				key: "year",
				label: t("admin.plans.columns.year"),
				truncate: false,
				render: (plan) => (
					<PriceCell price={plan.year} currency={plan.currency} />
				),
			},
			{
				key: "stripeProductId",
				label: t("admin.plans.columns.product"),
				render: (plan) => (
					<span className="font-mono text-xs text-neutral-400">
						{plan.stripeProductId ?? "—"}
					</span>
				),
			},
			{
				key: "id",
				label: "",
				width: "110px",
				render: (plan) => (
					<Button
						size="sm"
						variant="ghost"
						color="neutral"
						picto="pen"
						onClick={() => setEditing(plan)}
						data-testid={`plan-edit-${plan.code}`}
					>
						{t("actions.edit")}
					</Button>
				),
				truncate: false,
			},
		],
		[]
	);

	if (isLoading) return <Spinner />;
	if (error || !data)
		return <InfoMessage color="danger">{t("admin.error")}</InfoMessage>;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="text-sm text-neutral-500">
					{t("admin.plans.intro")}
				</p>
				<Button
					size="sm"
					picto="refresh"
					onClick={runSync}
					isLoading={sync.isPending}
					data-testid="admin-plans-sync"
				>
					{t("admin.plans.sync")}
				</Button>
			</div>
			{data.stripeError && (
				<InfoMessage
					color="warning"
					data-testid="admin-plans-stripe-error"
				>
					{t("admin.plans.stripeError", { error: data.stripeError })}
				</InfoMessage>
			)}
			<Table
				items={data.plans}
				getItemKey={(plan) => plan.code}
				columns={columns}
			/>
			{editing && (
				<AdminPlanEditModal
					plan={editing}
					onClose={() => setEditing(null)}
				/>
			)}
		</div>
	);
};
